const roles = [
  {
    company: 'Stay Healthy Zone (Nutrio LLC)',
    role: 'Senior Software Engineer',
    location: 'Remote',
    startDate: '2024/05/01',
    endDate: new Date(),
    dates: 'May 2024 - Present',
    logo: <img src="SHZ_Logo.jpg" alt="Stay Healthy Zone Logo" width="65px" />,
    logoBackgroundColor: 'white',
    link: 'https://www.stayhealthyzone.com/',
    details: [
      'Lead full-stack feature development and modernization across a legacy Rails, CoffeeScript, and React platform.',
      'Introduced RTK Query and established reusable frontend patterns to replace deprecated Backbone code.',
      'Rebuilt high-traffic workflow forms into modular, maintainable components with clearer structure and better performance.',
      'Converted desktop-only workflows into mobile-friendly experiences with responsive layouts and thoughtful UI interactions.',
      'Delivered white-label deployments for enterprise clients, including custom branding, configuration, and localization.',
      'Worked across Rails, PostgreSQL, React/CoffeeScript, AWS, and CI/CD pipelines to support platform-wide improvements.',
      'Helped maintain platform stability through code reviews, incremental refactoring, and dependency upgrades.',
    ],
    skills: [
      { skillName: 'Ruby-On-Rails', skillLevel: 3 },
      { skillName: 'CoffeeScript', skillLevel: 3 },
      { skillName: 'React', skillLevel: 3 },
      { skillName: 'Redux Toolkit', skillLevel: 3 },
      { skillName: 'PostgreSQL', skillLevel: 3 },
      { skillName: 'Jbuilder', skillLevel: 3 },
      { skillName: 'HAML', skillLevel: 3 },
      { skillName: 'Sass', skillLevel: 3 },
      { skillName: 'AWS', skillLevel: 2 },
      { skillName: 'Cloudinary', skillLevel: 3 },
      { skillName: 'Docker', skillLevel: 2 },
      { skillName: 'Git', skillLevel: 3 },
      { skillName: 'Webpack', skillLevel: 2 },
    ],
    learnMore: {
      relevantLinks: [
        {
          name: 'Stay Healthy Zone Website',
          url: 'https://www.stayhealthyzone.com/',
        },
        {
          name: 'Stay Healthy Zone on LinkedIn',
          url: 'https://www.linkedin.com/company/stay-healthy-zone',
        },
      ],
      projects: [
        {
          projectTitle: 'My Creations: Backbone → RTK Query Migration',
          toolsUsed: [
            'CoffeeScript',
            'React',
            'Redux Toolkit',
            'Ruby',
            'Rails',
            'Sass',
            'Jbuilder',
          ],
          participationLevel: 'Architected and Implemented',
          descriptionDetails: {
            userStory:
              'Users should be able to create custom foods, recipes, and meals easily across devices.',
            ac: [
              'Rebuilt three complex creation forms using RTK Query and reusable component patterns.',
              'Introduced standard data-fetching and caching patterns for future frontend development.',
              'Added responsive layouts and touch-friendly UI for full mobile support.',
              'Maintained full compatibility with existing Rails backend APIs.',
            ],
            conclusion:
              'Researched, architected, and executed a 5.5-month migration of three business-critical nutrition creation forms, establishing RTK Query as the foundation for all future frontend development. Replaced legacy Backbone logic with a reusable, component-driven system, touched 100+ files and resolved long-standing nutrient calculation issues. The result was cleaner code, consistent data handling, full feature parity, and fully mobile-ready forms that are now significantly easier to maintain and evolve.',
          },
        },
        {
          projectTitle: 'Miami-Dade County White-Label Deployment',
          toolsUsed: [
            'Ruby',
            'Rails',
            'PostgreSQL',
            'HAML',
            'Sass',
            'CoffeeScript',
          ],
          participationLevel: 'Implemented',
          descriptionDetails: {
            userStory:
              'Miami-Dade required a bilingual, branded wellness platform for employees and residents.',
            ac: [
              'Configured custom theming, navigation, and domains for English and Spanish deployments.',
              'Enabled 30+ platform modules including challenges, meal planning, and device integrations.',
              'Set up 9 wellness program tracks aligned with major metabolic and cardiovascular conditions.',
            ],
            conclusion:
              'Delivered a full white-label deployment for Miami-Dade County, including bilingual domains, custom branding, feature configuration, and nine tailored wellness program tracks. Built the backend cobrand structure, asset and locale packages, and enabled 30+ platform features. This launch demonstrated the platform’s enterprise scalability and provided a repeatable model for future large-scale client rollouts.',
          },
        },
        {
          projectTitle: 'Leaderboard Podium Visualization',
          toolsUsed: ['CoffeeScript', 'React', 'Sass'],
          participationLevel: 'Implemented',
          descriptionDetails: {
            userStory:
              'Users should be able to instantly see top performers in active wellness challenges.',
            ac: [
              'Built a podium-style leaderboard display highlighting the top 3 users.',
              'Integrated with existing leaderboard state and time range filters.',
              'Designed responsive layouts suitable for both desktop and mobile views.',
            ],
            conclusion:
              'Added a lightweight motivational feature that increased visibility and engagement in challenge participation.',
          },
        },
      ],
    },
  },
  {
    company: 'Valor Performance, Inc',
    role: 'Software Engineer & Scrum Master',
    location: 'Remote',
    startDate: '2020/11/01',
    endDate: '2024/02/01',
    dates: 'Nov 2020 - Feb 2024',
    logo: <img src="valor-logo.jpg" alt="Valor Logo" width="65px" />,
    logoBackgroundColor: 'black',
    link: 'https://www.valorperform.com/',
    details: [
      'Delivered consistent, high-quality React, Ruby-On-Rails, and PostgreSQL code across multiple product areas.',
      'Kept the codebase stable through thorough code reviews and quality assurance.',
      'Introduced a front-end testing suite for three React apps, improving stability and code confidence.',
      'Set up a Confluence documentation system that reduced onboarding time and cut down on repeated questions.',
      'Picked up new tools, packages, and languages quickly as project needs evolved.',
      'Adapted existing features to meet shifting business requirements with minimal turnaround.',
      'Ran Engineering Technical Talks to encourage knowledge-sharing and continuous learning.',
      "Led the team's transition from Kanban to Sprints for a more structured workflow.",
      'Organized a remote Scrum team of 6 engineers across varying backgrounds and timezones.',
      'Worked closely with Product to align engineering efforts with client needs.',
      'Refined tickets and improved estimation practices across the team.',
      'Collaborated with the product owner to plan and prioritize sprint work.',
      'Led regular training sessions and discussions on team expectations and best practices.',
      'Helped with hiring, including interviewing candidates.',
    ],
    skills: [
      { skillName: 'Ruby-On-Rails', skillLevel: 3 },
      { skillName: 'GraphQL', skillLevel: 3 },
      { skillName: 'React', skillLevel: 3 },
      { skillName: 'AWS', skillLevel: 1 },
      { skillName: 'Sidekiq', skillLevel: 3 },
      { skillName: 'Kubernetes', skillLevel: 2 },
      { skillName: 'Redis', skillLevel: 3 },
      { skillName: 'Postgres', skillLevel: 3 },
      { skillName: 'MySQL', skillLevel: 2 },
      { skillName: 'RabbitMQ', skillLevel: 3 },
      { skillName: 'RSPEC', skillLevel: 3 },
      { skillName: 'Jira', skillLevel: 3 },
      { skillName: 'Zapier', skillLevel: 3 },
      { skillName: 'Hubspot', skillLevel: 1 },
      { skillName: 'Docker', skillLevel: 1 },
      { skillName: 'Git', skillLevel: 3 },
    ],
    learnMore: {
      relevantLinks: [
        {
          name: "Valor's Marketing Site",
          url: 'https://www.valorperform.com/',
        },
        {
          name: 'Valor on LinkedIn',
          url: 'https://www.linkedin.com/company/valorperformance/',
        },
      ],
      projects: [
        {
          projectTitle: 'Viewpoints Service Rebuild',
          toolsUsed: [
            'Javascript',
            'React',
            'GraphQL',
            'Ruby',
            'Rails',
            'PostgreSQL',
          ],
          participationLevel: 'Implemented',
          descriptionDetails: {
            userStory:
              'As a Valor client I would like my peer review software to be easier to use, more impactful, and less time consuming.',
            ac: [
              'Eliminate the sign-up requirement for providing feedback to Valor participants, simplifying the process for new users.',
              "Reduce the number of clicks needed to leave feedback on someone's round of Viewpoints.",
              'Improve the user experience for viewing feedback from a round of Viewpoints.',
              'Update the language and dependencies to the newest stable versions, enabling the use of the latest features.',
            ],
            conclusion:
              'Helped rebuild Viewpoints, our peer review software. We cut the number of clicks to complete a review in half and worked with the content team to simplify the questions and improve the rating scale. These changes brought our completion rate from around 35% up to 70%, with an open rate of 80%.',
          },
        },
        {
          projectTitle: 'Frontend Testing Initiative',
          toolsUsed: ['Javascript', 'React', 'Jest'],
          participationLevel: 'Designed and Implemented',
          descriptionDetails: {
            userStory:
              'As an engineer at Valor Performance, I recognized the need for a comprehensive test suite for our front-end components to improve the quality and reliability of our code.',
            ac: [
              'Updated React/JavaScript versions to the newest stable releases to access the latest features.',
              'Wrote Jest and Enzyme tests for all base components to ensure functionality and integrity.',
              'Built a testing framework that made it easy to validate functional and display changes.',
            ],
            conclusion:
              'Grew frontend test coverage from 0% to 45% within a year. This gave the team more confidence when making changes, sped up development, and reduced the number of bugs reported by users.',
          },
        },
        {
          projectTitle: 'Automate Helpdesk Request Pipeline',
          toolsUsed: ['Jira', 'Zapier'],
          participationLevel: 'Designed and Implemented',
          descriptionDetails: {
            userStory:
              'As a Valor Performance Customer Success or Customer Service team member, I would like a single source of truth for our service requests to be funneled through and tracked.',
            ac: [
              'Requests from Jira Service Desk, Slack, and Direct Email populate into the Helpdesk Slack channel.',
              'Comments, status updates, and code changes related to the issue are easily tracked and displayed with the original issue details.',
              'Client and internal responses are immediately pushed to the correct Slack thread.',
            ],
            conclusion:
              'Implemented a Zapier integration to track Jira, Slack, and relevant CS gmail accounts, funneling all details into a single Slack thread. This led to a 30% drop in overall service requests, allowing team members to focus more on long-term solutions.',
          },
        },
      ],
    },
  },
  {
    company: 'Wayfair',
    role: 'Software Engineer L2',
    location: 'Boston, MA/Remote',
    startDate: '2018/02/01',
    endDate: '2020/11/01',
    dates: 'Feb 2018 - Nov 2020',
    logo: <img src="wayfair-logo.jpg" alt="Wayfair Logo" width="65px" />,
    logoBackgroundColor: '#7f1a7f',
    link: 'https://www.wayfair.com/',
    details: [
      "Built and maintained systems for Wayfair's product description data on the Naming & Descriptions team, using PHP, C#, Java, MySQL, and React.",
      "Earned certifications for reviewing JavaScript and PHP through Wayfair's internal certification process.",
      'Developed the product naming software that generates names across the entire website.',
      "Grew the team's Tech Talks into a company-wide program where employees from any department could present and discuss technologies, tools, and projects.",
      'Co-led the Accessibility@Work Employee Resource Group, creating a supportive space for disabled, chronically ill, and allied employees.',
      'Organized social and educational events in partnership with other ERGs and the Diversity, Equity, and Inclusion department.',
    ],
    skills: [
      { skillName: 'PHP', skillLevel: 3 },
      { skillName: 'React/Redux', skillLevel: 3 },
      { skillName: 'GraphQL', skillLevel: 3 },
      { skillName: 'C#/.NET', skillLevel: 2 },
      { skillName: 'SQL', skillLevel: 2 },
      { skillName: 'Java', skillLevel: 1 },
      { skillName: 'Git', skillLevel: 3 },
      { skillName: 'Kubernetes', skillLevel: 2 },
      { skillName: 'MySQL', skillLevel: 2 },
      { skillName: 'Jest', skillLevel: 2 },
      { skillName: 'Enzyme', skillLevel: 2 },
      { skillName: 'Docker', skillLevel: 2 },
    ],
    learnMore: {
      relevantLinks: [
        { name: 'Wayfair.com', url: 'https://www.wayfair.com/' },
        {
          name: 'Wayfair on LinkedIn',
          url: 'https://www.linkedin.com/company/wayfair/',
        },
      ],
      projects: [
        {
          projectTitle: 'Product Name Generator (Internal Tool)',
          toolsUsed: ['React', 'PHP', 'MySQL'],
          participationLevel: 'Architected and Implemented',
          descriptionDetails: {
            userStory:
              'As a Wayfair Product Owner, I need a simple, efficient way to automatically generate unique and descriptive names for our 14 million products.',
            ac: [
              'Product names can include any product details (color, material, etc.) in the generated name.',
              'Product names are unique on platform.',
              'Product owners can preview the name that will be generated for a given sku.',
              'Product name formulas can be copied and used in other sections for easy preview and tweaks of existing formulas.',
            ],
            conclusion:
              'Built an internal tool that let product owners create naming formulas for products. This cut down on the manual hours previously spent naming products and freed up time for more meaningful work. The tool makes it easy to try out and preview names, keeping product identities consistent across SKUs.',
          },
        },
      ],
    },
  },
  {
    company: 'Poughkeepsie Child Development Center',
    role: 'Lead Preschool Teacher',
    location: 'Poughkeepsie, NY',
    startDate: '2015/09/01',
    endDate: '2017/06/01',
    dates: 'Sept 2015 - Jun 2017',
    logo: <img src="pcdc-logo.jpeg" alt="pcdc Logo" width="65px" />,
    logoBackgroundColor: 'white',
    link: 'https://www.facebook.com/PCDC159/',
    details: [
      'Learned to explain complex ideas clearly to people encountering them for the first time.',
      'Designed lessons and projects for a wide range of ages and skill levels, tailoring the approach to each group.',
      'Handled behavioral challenges calmly, building a sense of teamwork and community in the classroom.',
      'Worked across cultural and linguistic differences, connecting with families from many backgrounds.',
    ],
    skills: [
      { skillName: 'Behavioral Management', skillLevel: 3 },
      { skillName: 'Communication', skillLevel: 3 },
      { skillName: 'Curriculum Development', skillLevel: 2 },
      { skillName: 'Multicultural Awareness', skillLevel: 2 },
      { skillName: 'Team Building', skillLevel: 3 },
      { skillName: 'Team Management', skillLevel: 3 },
      { skillName: 'Trainings & Education', skillLevel: 3 },
    ],
  },
  {
    company: 'The Millbrook Winery',
    role: 'Tour Guide/Sommelier',
    location: 'Millbrook, NY',
    startDate: '2013/05/01',
    endDate: '2017/01/01',
    dates: 'May 2013 - Jan 2017',
    logo: <img src="winery-logo.jpeg" alt="pcdc Logo" width="65px" />,
    logoBackgroundColor: 'white',
    link: 'https://www.millbrookwine.com/',
    details: [
      'Managed tour and tasting schedules to keep guests happy and staff running smoothly.',
      'Gave personalized wine recommendations that matched guests with bottles they loved.',
      'Provided warm, attentive service during busy winery events.',
      'Represented the winery at events and conferences, building community connections.',
    ],
    skills: [
      { skillName: 'Public Speaking', skillLevel: 3 },
      { skillName: 'Communication', skillLevel: 3 },
      { skillName: 'Team Building', skillLevel: 3 },
      { skillName: 'Time Management', skillLevel: 3 },
      { skillName: 'Trainings & Education', skillLevel: 3 },
    ],
  },
];

export default roles;
